
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { messagesService } from '../services/messagesService';
import { MessageFilter, CreateMessagePayload, UpdateMessagePayload, CreateReplyPayload } from '../types/messages';

function shouldRetryMessagesQuery(failureCount: number, error: any): boolean {
    const status = error?.status;
    if (status === 401 || status === 403 || status === 404 || status === 429) return false;
    return failureCount < 1;
}

// Chaves de query centralizadas para fácil invalidação
export const messageKeys = {
    all: ['messages'] as const,
    lists: () => [...messageKeys.all, 'list'] as const,
    list: (filters: MessageFilter, useAdminRoute = false) => [...messageKeys.lists(), { ...filters, useAdminRoute }] as const,
    details: () => [...messageKeys.all, 'detail'] as const,
    detail: (id: number) => [...messageKeys.details(), id] as const,
    unreadCount: ['unread-messages-count'] as const,
};

/**
 * Hook para buscar mensagens paginadas
 */
export function useMessages(filters: MessageFilter, enabled = true, useAdminRoute = false) {
    return useQuery({
        queryKey: messageKeys.list(filters, useAdminRoute),
        queryFn: () => messagesService.getAll(filters, useAdminRoute),
        placeholderData: keepPreviousData, // Mantém dados anteriores enquanto carrega nova página
        staleTime: 60 * 1000, // 1 minuto
        retry: shouldRetryMessagesQuery,
        refetchOnReconnect: true,
        enabled,
    });
}

/**
 * Hook para buscar contagem de não lidas
 */
export function useUnreadMessagesCount(enabled = true) {
    return useQuery({
        queryKey: messageKeys.unreadCount,
        queryFn: async () => {
            const response = await messagesService.getUnreadCount();
            return response.data;
        },
        staleTime: 30 * 1000, // 30 segundos
        retry: shouldRetryMessagesQuery,
        refetchOnReconnect: true,
        enabled,
    });
}

/**
 * Hook para buscar detalhes de uma mensagem
 */
export function useMessage(id: number, enabled = true) {
    return useQuery({
        queryKey: messageKeys.detail(id),
        queryFn: () => messagesService.getById(id),
        staleTime: 5 * 60 * 1000, // 5 minutos
        enabled: !!id && enabled,
    });
}

/**
 * Hook para marcar mensagem como lida
 */
export function useMarkMessageAsRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => messagesService.markAsRead(id),
        onMutate: async (_id: number) => {
            // Cancelar refetches pendentes para evitar conflito
            await queryClient.cancelQueries({ queryKey: messageKeys.unreadCount });

            // Snapshot do valor anterior para rollback
            const previousCount = queryClient.getQueryData<{ unread_count: number }>(messageKeys.unreadCount);

            // Decremento otimista: badge atualiza instantaneamente
            if (previousCount && previousCount.unread_count > 0) {
                queryClient.setQueryData(messageKeys.unreadCount, {
                    unread_count: previousCount.unread_count - 1,
                });
            }

            return { previousCount };
        },
        onError: (_err, _id, context) => {
            // Rollback: restaurar contagem anterior se request falhar
            if (context?.previousCount) {
                queryClient.setQueryData(messageKeys.unreadCount, context.previousCount);
            }
        },
        onSuccess: (data) => {
            // Corrigir com valor real do backend (source of truth)
            if (data.data?.unread_count !== undefined) {
                queryClient.setQueryData(messageKeys.unreadCount, { unread_count: data.data.unread_count });
            } else {
                queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount });
            }

            queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
        },
    });
}

/**
 * Hook para criar mensagem (Admin)
 */
export function useCreateMessage() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: CreateMessagePayload) => messagesService.create(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
        },
    });
}

/**
 * Hook para atualizar mensagem (Admin)
 */
export function useUpdateMessage() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }: { id: number; payload: UpdateMessagePayload }) =>
            messagesService.update(id, payload),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
            queryClient.invalidateQueries({ queryKey: messageKeys.detail(variables.id) });
        },
    });
}

/**
 * Hook para excluir mensagem (Admin)
 */
export function useDeleteMessage() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => messagesService.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
            queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount });
        },
    });
}

/**
 * Hook para responder mensagem
 */
export function useReplyMessage() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }: { id: number; payload: CreateReplyPayload }) =>
            messagesService.sendReply(id, payload),
        onSuccess: (data, variables) => {
            // Invalidar detalhes da mensagem para atualizar status de resposta
            queryClient.invalidateQueries({ queryKey: messageKeys.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
        },
    });
}

/**
 * ✅ NOVO: Hook para marcar múltiplas msgs como lidas
 */
export function useMarkMultipleMessagesAsRead() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (ids: number[]) => messagesService.markMultipleAsRead(ids),
        onSuccess: (data) => {
            // Atualizar contagem de não lidas com o valor retornado
            if (data.data?.unread_count !== undefined) {
                queryClient.setQueryData(messageKeys.unreadCount, { unread_count: data.data.unread_count });
            } else {
                queryClient.invalidateQueries({ queryKey: messageKeys.unreadCount });
            }
            queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
        },
    });
}
